import React, { useState, useEffect, useCallback } from 'react';
import { toast } from 'react-hot-toast';
import { userQuotaAPI } from '../../services/userQuotaAPI';
import { getQuotaStatus, getQuotaClasses } from '../../config/appConfig';

const QuotaManagementModal = ({ 
  show, 
  onClose, 
  selectedUser,
  onQuotaUpdated 
}) => {
  const [quotaData, setQuotaData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [documentsToAdd, setDocumentsToAdd] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const loadUserQuota = useCallback(async () => {
    if (!selectedUser) return;
    
    setLoading(true);
    try {
      // Usar la cuota ya disponible del usuario seleccionado
      if (selectedUser.quota) {
        setQuotaData(selectedUser.quota);
      } else {
        // Si no hay cuota en el usuario, mostrar datos por defecto
        setQuotaData({
          documents_used: 0,
          total_documents_purchased: 0,
          available_documents: 0,
          percentage_used: 0
        });
      }
    } catch (error) {
      console.error('Error al cargar cuota:', error);
      toast.error('Error al cargar información de cuota');
      setQuotaData(null);
    } finally {
      setLoading(false);
    }
  }, [selectedUser]);

  // Cargar datos de cuota cuando se abre el modal
  useEffect(() => {
    if (show && selectedUser) {
      loadUserQuota();
    }
  }, [show, selectedUser, loadUserQuota]);

  const handleAddQuota = async (e) => {
    e.preventDefault();
    
    if (!documentsToAdd || documentsToAdd <= 0) {
      toast.error('Ingresa una cantidad válida de documentos');
      return;
    }

    setSubmitting(true);
    try {
      // Usar la API del UserQuotaAPI para agregar cuota
      const result = await userQuotaAPI.addQuota(selectedUser.id, parseInt(documentsToAdd));
      
      if (result.success) {
        // Actualizar datos locales con la respuesta del servidor
        const updatedQuota = result.data.data || result.data;
        setQuotaData(updatedQuota);
        setDocumentsToAdd('');
        
        toast.success(`Se agregaron ${documentsToAdd} documentos a ${selectedUser.full_name}`);
        
        // Notificar al componente padre para actualizar la lista
        if (onQuotaUpdated) {
          onQuotaUpdated(selectedUser.id, updatedQuota);
        }
      } else {
        throw new Error(result.error || 'Error en la respuesta del servidor');
      }
    } catch (error) {
      console.error('Error al agregar cuota:', error);
      toast.error(`Error al agregar cuota: ${error.message || 'Error desconocido'}`);
    } finally {
      setSubmitting(false);
    }
  };

  const formatQuotaInfo = (quota) => {
    if (!quota) return null;
    
    return userQuotaAPI.formatQuotaInfo(quota);
  };

  if (!show) return null;

  const quotaInfo = formatQuotaInfo(quotaData);
  
  // Obtener estado y clases de la cuota usando configuración centralizada
  const quotaStatus = quotaData ? getQuotaStatus(quotaData.available_documents) : null;
  const quotaClasses = quotaData ? getQuotaClasses(quotaData.available_documents) : null;

  return (
    <div className="modal fade show d-block" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
      <div className="modal-dialog modal-lg">
        <div className="modal-content">
          <div className="modal-header">
            <h5 className="modal-title">
              <i className="bi bi-wallet2 me-2"></i>
              Gestionar Cuota - {selectedUser?.full_name}
            </h5>
            <button 
              type="button" 
              className="btn-close" 
              onClick={onClose}
            ></button>
          </div>
          
          <div className="modal-body">
            {loading ? (
              <div className="text-center py-4">
                <div className="spinner-border text-primary"></div>
                <p className="mt-2 text-muted">Cargando información de cuota...</p>
              </div>
            ) : quotaData && quotaInfo ? (
              <>
           
                {/* Estado de la cuota */}
                <div className="mb-4">
                  <div className="d-flex justify-content-between align-items-center mb-2">
                    <span className="fw-medium">Estado de la Cuota</span>
                    <strong>{quotaInfo.used} usados / {quotaInfo.available} restantes</strong>
                  </div>
                  
                  {/* Indicador visual basado en documentos restantes */}
                  <div className="row g-2">
                    <div className="col-6">
                      <div className={`alert mb-0 ${quotaClasses ? quotaClasses.alert : 'alert-success'}`}>
                        <div className="d-flex align-items-center">
                          <i className={`bi ${quotaStatus ? quotaStatus.icon : 'bi-check-circle-fill'} me-2`}></i>
                          <div>
                            <strong>
                              {quotaStatus ? quotaStatus.label : 'Cuota Saludable'}
                            </strong>
                            <div className="small">
                              {quotaStatus ? quotaStatus.message : 'Usuario con suficientes documentos'}
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                    <div className="col-6">
                      <div className="alert alert-info mb-0">
                        <div className="d-flex align-items-center">
                          <i className="bi bi-graph-up me-2"></i>
                          <div>
                            <strong>Total Adquirido</strong>
                            <div className="small">{quotaInfo.total} documentos en total</div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Formulario para agregar documentos */}
                <div className="border-top pt-4">
                  <h6 className="fw-bold mb-3">Agregar Documentos</h6>
                  <form onSubmit={handleAddQuota}>
                    <div className="row g-3">
                      <div className="col-12">
                        <label htmlFor="documentsToAdd" className="form-label">
                          Cantidad de documentos a agregar: <span className="text-primary fw-bold">{documentsToAdd || 0}</span>
                        </label>
                        
                        {/* Slider para seleccionar cantidad */}
                        <div className="mb-3">
                          <input
                            type="range"
                            className="form-range"
                            min="100"
                            max="1000"
                            step="10"
                            value={documentsToAdd || 0}
                            onChange={(e) => setDocumentsToAdd(e.target.value)}
                            id="documentsSlider"
                          />
                          <div className="d-flex justify-content-between text-muted small mt-1">
                            <span>100</span>
                            <span>250</span>
                            <span>500</span>
                            <span>750</span>
                            <span>1000</span>
                          </div>
                        </div>

                        {/* Input manual para casos específicos */}
                        <div className="row g-2">
                          <div className="col-8">
                            <input
                              type="number"
                              id="documentsToAdd"
                              className="form-control"
                              value={documentsToAdd}
                              onChange={(e) => setDocumentsToAdd(e.target.value)}
                              min="1"
                              max="10000"
                              step="1"
                              placeholder="O ingresa manualmente"
                            />
                            <div className="form-text">
                              Rango: 1 - 10,000 documentos
                            </div>
                          </div>
                          <div className="col-4">
                            <button 
                              type="submit" 
                              className="btn btn-success w-100"
                              disabled={submitting || !documentsToAdd || documentsToAdd <= 0}
                            >
                              {submitting ? (
                                <>
                                  <span className="spinner-border spinner-border-sm me-2"></span>
                                  Agregando...
                                </>
                              ) : (
                                <>
                                  <i className="bi bi-plus-circle me-2"></i>
                                  Agregar
                                </>
                              )}
                            </button>
                          </div>
                        </div>
                      </div>
                    </div>
                  </form>
                </div>
              </>
            ) : (
              <div className="text-center py-4">
                <i className="bi bi-exclamation-circle text-muted" style={{ fontSize: '3rem' }}></i>
                <p className="mt-2 text-muted">No se pudo cargar la información de cuota</p>
                <button className="btn btn-outline-primary" onClick={loadUserQuota}>
                  <i className="bi bi-arrow-clockwise me-2"></i>
                  Reintentar
                </button>
              </div>
            )}
          </div>
          
          <div className="modal-footer">
            <button 
              type="button" 
              className="btn btn-secondary" 
              onClick={onClose}
            >
              Cerrar
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default QuotaManagementModal;
